import { createStore } from 'hox';
import { useWallets } from '../wallet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trpc, trpcApi } from '@bitbot/api';
import { debounce } from 'lodash';
import Big from 'big.js';
import { useDex } from './index';
import { formatValue } from '@bitbot/shared/utils/quote-value';
import { stringToNumber } from '@bitbot/shared/utils';
import { Notify } from '@bitbot/ui-design';
import { useOrderConfimDialog } from './common';
import { usecustomAssetWithParams } from '../wallet/manage-asset';
import { useGlobalValueStore } from './common';
import { useToken } from '../user/token';
import { useAuthentication } from './common';
import { useLogout } from '../user';
import { sendGAEvent } from '@next/third-parties/google';

type EstimateGas = {
  wallet: string;
  gasPrice: number;
  gas: any;
  gasShow: string;
  nativePrice: number;
  gasUsd: number;
  amount_out_min: number;
  amount_out: number;
  tokenInPriceUsd: number;
  tokenOutPriceUsd: number;
  amount_in_usd: number;
  amount_out_usd: number;
  gasUnit: number;
};

interface EstimateParams {
  wallet: string;
  token_in: string;
  token_out: string;
  amount_in: number;
  orderType?: 'market' | 'limit';
  pair?: string | undefined;
  max_spl?: number | undefined;
  expiration?: number | undefined;
  dex_type: 'uniswap-v2' | 'uniswap-v3';
  kind: 'buy' | 'sell';
  priceRate?: number;
  moralisGetPriceUsd?: boolean;
}

interface PlaceOrderParams extends EstimateParams {
  gasLimit?: number;
  gasPrice?: number;
}

export const useOpenOrders = () => {
  const { chain, currentWallet } = useWallets();
  const { data: history, refetch: refetchOpenOrders } =
    trpc.order.getAllOrder.useQuery(
      {
        state: 'open',
        chain: chain.name,
        limit: 20,
        address: currentWallet!,
        showLogo: true,
      },
      {
        enabled: !!currentWallet,
        refetchInterval: 10000,
      },
    );

  return {
    history,
    refetchOpenOrders,
  };
};

export const [usePlaceOrder, PlaceOrderProvider] = createStore(() => {
  const { hasTradeLogin } = useToken();
  const gasPrice = useGasPrice();
  const {
    authCode,
    setAuthCode,
    active,
    setActive,
    otpCodeError,
    setOtpCodeError,
  } = useAuthentication();
  const { currentWallet, walletAssetConfigObj, chain } = useWallets();
  const { setShowApproveTokenDialog, setShowCreateWalletAndLoginDialog } =
    useGlobalValueStore();
  const countRef = useRef(0);
  const { pairInfo, token, quoteToken, pairAddress } = useDex();
  const [isBuy, setIsBuy] = useState(true);
  const [isMarket, setIsMarket] = useState(true); //true 市价订单， false 限价订单
  const [limitRate, setLimitRate] = useState(''); // 限价订单时, 触发价格百分比，
  const [amount, setAmount] = useState('');
  const [isMax, setIsMax] = useState(false);
  const [amountToken, setAmountToken] = useState(quoteToken); // amount的币种

  const [slippageType, setSlippageType] = useState<0 | 1>(0); // 0 no limit,  1 custom
  const [customSpl, setCustomSpl] = useState('5');
  const [spl, setSpl] = useState('Unlimited');
  const { customAssetWithParams } = usecustomAssetWithParams();
  const { setShowOrderConfirm } = useOrderConfimDialog();
  const [isUpdateSlippage, setIsUpdateSlippage] = useState(false); //控制滑点设置的弹窗

  // const { addWhitelist, whitelistData, addingWhitelist } =
  //   useTradeWhitelist();
  const { handleInvalidToken } = useLogout();
  const [whitelistStatus, setWhitelistStatus] =
    useState<Record<string | number, boolean>>();
  const addWhitelistMutation = trpc.whitelist.addWhitelist.useMutation();
  const [addingWhitelist, setAddingWhitelist] = useState(false);
  const checkWhitelistTokensMutation =
    trpc.whitelist.checkWhitelistTokens.useMutation();
  const addWhitelist = (otp: string, addresses: string[]) => {
    setAddingWhitelist(true);
    addWhitelistMutation
      .mutateAsync({
        otp,
        ksAddress: currentWallet!,
        type: 'token',
        chain: chain.name,
        addresses,
      })
      .then(data => {
        if (data.isSuccess) {
          setTimeout(() => {
            checkWhitelistTokens();
          }, 2000);
        } else {
          setAddingWhitelist(false);
          if (data.msg === 'Invalid OTP') {
            setOtpCodeError(true);
          } else {
            Notify.error(data.msg);
          }
        }
      })
      .catch(e => {
        handleInvalidToken(e.message as string);
        setAddingWhitelist(false);
      });
  };

  const checkWhitelistTokens = () => {
    checkWhitelistTokensMutation
      .mutateAsync({
        ksAddress: currentWallet!,
        chain: chain.name,
        addresses: [
          pairInfo!.baseToken.address,
          pairInfo!.quoteToken.address,
        ],
      })
      .then(data => {
        let values = Object.values(data);
        if (values.includes(false)) {
          setTimeout(() => {
            checkWhitelistTokens();
          }, 2000);
        } else {
          setWhitelistStatus(data);
          setAddingWhitelist(false);
          setShowApproveTokenDialog(false);
          setAddingWhitelist(false);
          Notify.success('Token has been approved');
        }
      })
      .catch(e => {
        setTimeout(() => {
          checkWhitelistTokens();
        }, 2000);
      });
  };

  //初始获取两个token是否已经加入白名单
  useEffect(() => {
    if (!!currentWallet && !!pairInfo) {
      checkWhitelistTokensMutation
        .mutateAsync({
          ksAddress: currentWallet,
          chain: chain.name,
          addresses: [
            pairInfo.baseToken.address,
            pairInfo.quoteToken.address,
          ],
        })
        .then(data => {
          setWhitelistStatus(data);
        })
        .catch(e => {
          handleInvalidToken(e.message as string);
        });
    }
  }, [pairInfo, currentWallet]);

  const whitelistData = useMemo(() => {
    if (!!whitelistStatus && !!pairInfo) {
      const baseTokenStatus = whitelistStatus[pairInfo.baseToken.address];
      const quoteTokenStatus =
        whitelistStatus[pairInfo.quoteToken.address];
      return {
        base: baseTokenStatus,
        quote: quoteTokenStatus,
        status: baseTokenStatus && quoteTokenStatus,
        fetching: false,
      };
    } else {
      return {
        fetching: true,
      };
    }
  }, [whitelistStatus, pairInfo]);

  const [expiryTime, setExpiryTime] = useState<{
    num: number;
    unit: string;
    value: number;
  }>({ num: 1, unit: 'Day', value: 24 * 60 * 60 });

  const [estimate, setEstimate] = useState('--');
  const [rates, setRates] = useState(0);
  const [isLoading, setIsLoading] = useState(false); //调用estiamte gas， 完成后跳转confirm 页面
  const [isPlacing, setIsPlacing] = useState(false);
  const { history, refetchOpenOrders } = useOpenOrders();
  const {
    data: baseBalance,
    refetch: refetchBaseBalance,
    isFetching: gettingBaseBalance,
  } = trpc.wallet.getTokenBalance.useQuery(
    {
      wallet: currentWallet!,
      tokenAddress: pairInfo?.baseToken.address!,
    },
    {
      enabled:
        !!currentWallet && !!pairInfo?.baseToken.address && hasTradeLogin,
    },
  );

  const {
    data: quoteBalance,
    refetch: refetchQuoteBalance,
    isFetching: gettingQuoteBalance,
  } = trpc.wallet.getTokenBalance.useQuery(
    {
      wallet: currentWallet!,
      tokenAddress: pairInfo?.quoteToken.address!,
    },
    {
      enabled: !!currentWallet && !!pairInfo?.quoteToken.address,
    },
  );

  useEffect(() => {
    //币对变了, 要初始化所有数据
    setIsBuy(true);
    setIsMarket(true);
    setLimitRate('');
    setAmount('');
    setSlippageType(0);
    setCustomSpl('5');
    setSpl('Unlimited');
    setEstimate('--');
    setRates(0);
    setExpiryTime({ num: 1, unit: 'Day', value: 24 * 60 * 60 });
    setAmountToken(quoteToken);
    countRef.current = 0;
  }, [token, quoteToken, pairAddress]);

  //获取rate
  const { data: rate, isSuccess: hasGotRate } =
    trpc.transfer.getTokenAmount.useQuery(
      {
        token_in: pairInfo?.baseToken.address!,
        token_out: pairInfo?.quoteToken.address!,
        amount_in: 0.001, // set it to 0.001 to avoid low price with low liquidity pool
        chain: 'base',
        dex_type:
          pairInfo?.dexVersion === 'v3' ? 'uniswap-v3' : 'uniswap-v2',
      },
      {
        enabled: !!pairInfo && hasTradeLogin,
      },
    );

  const handleAmountChange = useCallback(
    debounce((amount: string, limitRate: string) => {
      countRef.current++;
      if (+amount <= 0 || !rate || Number.isNaN(+amount)) {
        setRates(0);
        setEstimate('--');
        return;
      }
      if (isMarket) {
        const count = countRef.current;
        if (amountToken === token) {
          if (isBuy) {
            setEstimate(amount);
          } else {
            trpcApi.transfer.getTokenAmount
              .query({
                token_in: pairInfo?.baseToken.address!,
                token_out: pairInfo?.quoteToken.address!,
                amount_in: +amount,
                dex_type:
                  pairInfo?.dexVersion === 'v3'
                    ? 'uniswap-v3'
                    : 'uniswap-v2',
              })
              .then(data => {
                if (countRef.current > count) return;
                setEstimate(data.amountout.toString());
                setRates(Big(data.amountout).div(Big(+amount)).toNumber());
              });
          }
        } else {
          if (isBuy) {
            trpcApi.transfer.getTokenAmount
              .query({
                token_in: pairInfo?.quoteToken.address!,
                token_out: pairInfo?.baseToken.address!,
                amount_in: +amount,
                dex_type:
                  pairInfo?.dexVersion === 'v3'
                    ? 'uniswap-v3'
                    : 'uniswap-v2',
              })
              .then(data => {
                if (countRef.current > count) return;
                setEstimate(data.amountout.toString());
                setRates(
                  Big(1)
                    .div(Big(data.amountout).div(Big(+amount)))
                    .toNumber(),
                );
              });
          } else {
            setEstimate(amount);
          }
        }
      } else {
        if (+limitRate === 0 || Number.isNaN(+limitRate)) {
          setEstimate('--');
          return;
        }
        setRates(+limitRate);
        if (isBuy) {
          if (amountToken === token) {
            setEstimate(amount);
          } else {
            setEstimate(Big(amount).div(Big(limitRate)).toString());
          }
        } else {
          if (amountToken === token) {
            setEstimate(Big(amount).times(Big(limitRate)).toString());
          } else {
            setEstimate(amount);
          }
        }
      }
    }, 300),
    [isMarket, isBuy, amountToken, limitRate, pairInfo, rate],
  );

  const _setAmount = (amount: string) => {
    setAmount(amount);
    handleAmountChange(amount, limitRate);
    setIsMax(false);
  };

  const _setLimitRate = (limitRate: string) => {
    setLimitRate(limitRate);
    handleAmountChange(amount, limitRate);
  };

  const _setAmountToken = (token: string) => {
    setAmountToken(token);
    _setAmount('');
  };

  const initValue = useCallback(() => {
    setLimitRate('');
    _setAmount('');
    setCustomSpl('5');
    setSpl('Unlimited');
  }, []);

  const _setIsBuy = (value: boolean) => {
    setIsBuy(value);
    initValue();
    if (value) {
      setAmountToken(quoteToken);
    } else {
      setAmountToken(token);
    }
  };

  const _setIsMarket = (value: boolean) => {
    initValue();
    setIsMarket(value);
  };

  const [estimateGasData, setEstimateGasData] = useState<EstimateGas>();

  const estimateGas = () => {
    const params: EstimateParams = {
      wallet: currentWallet!,
      token_in: isBuy
        ? pairInfo?.quoteToken.address!
        : pairInfo?.baseToken.address!,
      token_out: isBuy
        ? pairInfo?.baseToken.address!
        : pairInfo?.quoteToken.address!,
      amount_in: +realAmount,
      orderType: isMarket ? 'market' : 'limit',
      kind: isBuy ? 'buy' : 'sell',
      max_spl: slippageType === 0 ? 99 : +customSpl,
      dex_type: (pairInfo?.dexId + '-' + pairInfo?.dexVersion) as
        | 'uniswap-v2'
        | 'uniswap-v3',
      pair: pairAddress!,
      moralisGetPriceUsd: true,
    };

    if (!isMarket) {
      params.priceRate = +limitRate;
    }
    return trpcApi.order.placeOrderEstimateGas.query(params);
  };

  const placeOrderMutation = trpc.order.placeOrder.useMutation({});

  //当前选中币的真实余额
  const _balance = useMemo(() => {
    return amountToken === token
      ? baseBalance?.balanceFormat
      : quoteBalance?.balanceFormat;
  }, [amountToken, token, baseBalance, quoteBalance]);
  const _rate = (rate?.amountout ?? 0) * 1000 /* ?? '' */; // previous set rate to 0.0001, now need to times up to 1

  //当前选中币的可交易余额, 如果是ETH, 额外扣除0.00005
  const realBalance = useMemo(() => {
    return _balance
      ? amountToken === 'ETH'
        ? +_balance < 0.00005
          ? '0'
          : +_balance - 0.00005
        : _balance
      : '0';
  }, [amountToken, _balance]);
  //1.将现有token的余额根据rate转换成quoteToken的数量
  const _tokenBalance = formatValue(
    Big(baseBalance?.balanceFormat ?? 0)
      .times(
        Big(isMarket ? stringToNumber(_rate) : stringToNumber(limitRate)),
      )
      .toString(),
    6,
  );
  //如果是买，且quoteToken是ETH, 此时需要将quoteToken的balance余额减去0.00005， 然后再计算转换成token的数量
  const quoteBalanceFormated = quoteBalance?.balanceFormat
    ? +quoteBalance?.balanceFormat
    : 0;
  const calQuoteTokenBalance =
    isBuy && quoteToken === 'ETH'
      ? quoteBalanceFormated > 0.00005
        ? quoteBalanceFormated - 0.00005
        : 0
      : quoteBalanceFormated;
  //将现有quoteToken的余额根据rate转换成token的数量
  const _quoteBalance = isMarket
    ? +_rate === 0 || Number.isNaN(+_rate)
      ? '0'
      : formatValue(
          Big(calQuoteTokenBalance).div(Big(_rate)).toString(),
          6,
        )
    : +limitRate === 0 || Number.isNaN(+limitRate)
    ? '0'
    : formatValue(
        Big(calQuoteTokenBalance).div(Big(limitRate)).toString(),
        6,
      );
  const balance = isBuy
    ? amountToken === token
      ? _quoteBalance
      : realBalance
    : amountToken === quoteToken
    ? _tokenBalance
    : realBalance;

  const realAmount = useMemo(() => {
    let realRate = 0;
    if (isMarket) {
      realRate = (rate?.amountout ?? 0) * 1000; // previous set rate to 0.0001, now need to times up to 1
    } else {
      realRate = +limitRate;
    }
    if (realRate === 0) return '';
    if (isBuy && token === amountToken) {
      return String(+amount * realRate);
    }
    if (!isBuy && quoteToken === amountToken) {
      return +amount / realRate;
    }
    if (isMax) {
      if (
        isBuy &&
        amountToken === quoteToken &&
        amount === formatValue(realBalance, 6)
      ) {
        return realBalance;
      }
      if (
        !isBuy &&
        amountToken === token &&
        amount === formatValue(realBalance, 6)
      ) {
        return realBalance;
      }
    }

    return amount;
  }, [
    amount,
    rate,
    limitRate,
    isBuy,
    isMarket,
    amountToken,
    token,
    quoteToken,
    quoteBalanceFormated,
    isMax,
  ]);

  const _placeOrder = useMemo(
    () =>
      debounce(async (success: () => void) => {
        const params: PlaceOrderParams = {
          wallet: currentWallet!,
          token_in: isBuy
            ? pairInfo?.quoteToken.address!
            : pairInfo?.baseToken.address!,
          token_out: isBuy
            ? pairInfo?.baseToken.address!
            : pairInfo?.quoteToken.address!,
          amount_in: +realAmount,
          orderType: isMarket ? 'market' : 'limit',
          kind: isBuy ? 'buy' : 'sell',
          max_spl: slippageType === 0 ? 99 : +customSpl,
          dex_type: (pairInfo?.dexId + '-' + pairInfo?.dexVersion) as
            | 'uniswap-v2'
            | 'uniswap-v3',
          // gasPrice,
          // gasLimit: isMarket ? estimateGasData?.gasUnit! : 2000000,
        };

        if (!isMarket) {
          params.priceRate = +limitRate;
          params.expiration =
            expiryTime.unit === 'ms' //自定义时间
              ? expiryTime.num
              : Date.now() + expiryTime.value * 1000; //选择的过期时间
        }
        setIsPlacing(true);
        sendGAEvent(
          'event',
          `click_${pairInfo!.baseToken.symbol}_${
            pairInfo!.quoteToken.symbol
          }_confirm`,
        );
        return placeOrderMutation
          .mutateAsync(params)
          .then(() => {
            success();
            _setAmount('');
            setTimeout(() => {
              refetchOpenOrders();
            }, 1000);
            Notify.success('Place order successful');
            //交易成功后， 判断该币有没有在asset config里面，如果不在里面就要添加进去
            //baseToken
            const tokenAddress = pairInfo?.baseToken.address;
            const quoteAddress = pairInfo?.quoteToken.address;
            const tokens: {
              address: string;
              isShow: boolean;
              name?: string;
              symbol?: string;
              decimals?: number;
            }[] = [];
            if (!walletAssetConfigObj[tokenAddress!]) {
              //baseToken
              if (pairInfo?.baseToken.symbol !== 'ETH') {
                //过滤掉 ETH
                tokens.push({
                  address: tokenAddress!,
                  isShow: true,
                  name: pairInfo?.baseToken.name,
                  symbol: pairInfo?.baseToken.symbol,
                });
              }
            }
            //quoteToken
            if (!walletAssetConfigObj[quoteAddress!]) {
              if (pairInfo?.quoteToken.symbol !== 'ETH') {
                //过滤掉 ETH
                tokens.push({
                  address: quoteAddress!,
                  isShow: true,
                  name: pairInfo?.quoteToken.name,
                  symbol: pairInfo?.quoteToken.symbol,
                });
              }
            }
            if (tokens.length !== 0) {
              customAssetWithParams(tokens);
            }
            setTimeout(() => {
              refetchBaseBalance();
              refetchQuoteBalance();
            }, 2000);
          })
          .catch(error => {
            Notify.error(String(error));
          })
          .finally(() => {
            setIsPlacing(false);
          });
      }, 300),
    [
      currentWallet,
      pairInfo,
      slippageType,
      customSpl,
      limitRate,
      expiryTime,
      estimateGasData,
      isMarket,
      realAmount,
    ],
  );

  //点击main button contine时触发的点击事件
  const handleContinueClick = useCallback(() => {
    //判断用户是否已经绑定2fa获取trade授权
    if (!hasTradeLogin) {
      setShowCreateWalletAndLoginDialog(true);
      return;
    }
    //判断baseToken和quoteToken是否都已经授权
    if (!whitelistData.status) {
      sendGAEvent('event', 'click_approve');
      setShowApproveTokenDialog(true);
      return;
    }
    if (amount === '' || Number.isNaN(+amount) || +amount <= 0) {
      return;
    }
    setShowOrderConfirm(true);
    sendGAEvent('event', `trade_${token}_${quoteToken}_confirm`);
    if (isMarket) {
      setIsLoading(true);
      estimateGas()
        .then(data => {
          setEstimateGasData(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    isMarket,
    amount,
    limitRate,
    customSpl,
    expiryTime,
    hasTradeLogin,
    whitelistData,
    token,
    quoteToken,
  ]);

  return {
    isMarket,
    setIsMarket: _setIsMarket,
    isBuy,
    setIsBuy: _setIsBuy,
    setSlippageType,
    slippageType,
    setCustomSpl,
    customSpl,
    setSpl,
    spl,
    amount,
    realAmount,
    setAmount: _setAmount,
    limitRate,
    setLimitRate: _setLimitRate,
    amountToken,
    setAmountToken: _setAmountToken,
    expiryTime,
    setExpiryTime,
    estimate,
    rates,
    handleAmountChange,
    rate: _rate,
    hasGotRate,
    balance: Number.isNaN(+balance) ? 0 : balance,
    estimateGas,
    setEstimateGasData,
    estimateGasData,
    placeOrder: _placeOrder,
    setIsLoading,
    isLoading,
    isPlacing,
    setIsPlacing,
    baseBalance,
    quoteBalance,
    history,
    handleContinueClick,
    isUpdateSlippage,
    setIsUpdateSlippage,
    gettingBaseBalance,
    gettingQuoteBalance,
    whitelistData,
    addWhitelist,
    addingWhitelist,
    gasPrice,
    authCode,
    setAuthCode,
    active,
    setActive,
    otpCodeError,
    setIsMax,
  };
});

export const useGasPrice = () => {
  const { data: gasPrice } = trpc.wallet.getGasPrice.useQuery(
    {
      chain: 'base',
    },
    {
      retry: 5,
    },
  );
  return gasPrice;
};
